import BN from "bn.js";
import { produce } from "immer";
import isPlainObject from "is-plain-obj";

import { invert as _invert, map, mapValues, reduce, values } from "lodash";
import { useCallback, useMemo } from "react";
import { DeepPartial } from "redux";
import { None, Option, Some } from "space-monad";
import { AnySchema } from "yup";
import { ValidateOptions } from "yup/lib/types";
import { Fetchable, FetchableSuccess } from "../store/common/fetchable/helpers";
import { countries } from "../store/common/models";

/**
 * This method will return Some if at least one of the Options is Some
 * @param options A list of Option from space-monad
 * @returns An option
 */
export function any<T>(options: Option<any>[]): Option<T> {
  return options.reduceRight(
    (prev, curr) =>
      prev.fold(
        () => curr,
        () => prev
      ),
    None
  );
}

export async function mapValuesAsync<K, T, U extends PropertyKey>(
  rec: Record<U, T>,
  callback: (item: T, key: U) => Promise<K>
): Promise<Record<U, K>> {
  const result = await Promise.all(
    Object.entries<T>(rec).map(
      async ([key, value]) =>
        [key, await callback(value, key as U)] as [U, Awaited<K>]
    )
  );
  return result.reduce(
    (prev, [key, value]) => ({ ...prev, [key]: value }),
    {}
  ) as Record<U, K>;
}

/**
 * Typed Object.keys()
 */
export function keys<K extends PropertyKey>(
  r: Partial<Record<K, any>>
): Array<K> {
  return Object.keys(r) as Array<K>;
}

/** Typed invert */
export function invert<T extends PropertyKey, B extends PropertyKey>(
  myObj: Record<T, B>
) {
  return _invert(myObj) as Partial<Record<B, T>>;
}

/**
 * Typed map over object
 * @param object
 * @param iterator
 * @returns
 */
export function mapObject<F, K extends PropertyKey = string, T = any>(
  object: Partial<Record<K, T>>,
  iterator: (value: T, key: K) => F
) {
  const myKeys = keys(object);
  return map(myKeys, (x) => iterator(object[x] as T, x));
}

/**
 * Typed reduce over object
 * @param object
 * @param iterator
 * @returns
 */
export function reduceObject<K extends PropertyKey, T, Z>(
  object: Partial<Record<K, T>>,
  iterator: (acc: Z, value: T, key: K) => any,
  seed: Z
) {
  const myKeys: K[] = (keys(object) as any) as K[];
  return reduce(myKeys, (acc, x) => iterator(acc, object[x] as T, x), seed);
}

/**
 * Creates an object from a list of strings to retrieve a type having all entries as union.
 * E.g.
 *
 * const wow = createEnum('foo', 'bar')
 * type Wow = typeof wow.T
 * // Wow is of type: 'foo' | 'bar'
 *
 * @param s array of unioned types
 * @returns Object with T (type) and values
 */
export type CreateEnum<T extends string, K extends string> = (
  obj: Record<T, K>
) => {
  T: T;
  values: K;
  enum: Record<T, K>;
  keys: T;
};

export function createEnum<T extends string, K extends string>(
  obj: Record<T, K>
) {
  return {
    T: (Object.create(null) as any) as T,
    values: values<K>(obj),
    enum: obj,
    keys: keys<T>(obj),
  };
}

export function useYupValidationResolver<T extends Record<PropertyKey, any>>(
  validationSchema: AnySchema<T>,
  validationOptionsOpt?: ValidateOptions<T>
) {
  const validationOptions = useMemo(
    () =>
      validationOptionsOpt || {
        abortEarly: false,
        strict: true,
      },
    [validationOptionsOpt]
  );

  return useCallback(
    async (data) => {
      const compactedData = deepOmitBy(
        data,
        (val) =>
          val === "" ||
          val === null ||
          (isPlainObject(val) && Object.keys(val).length < 1)
      );

      try {
        const values = await validationSchema.validate(
          compactedData,
          validationOptions
        );

        return {
          values,
          errors: {},
        };
      } catch (errors: any) {
        if (errors.inner === undefined) throw errors;
        return {
          values: {},
          errors: errors.inner.reduce(
            (allErrors: any, currentError: any) => ({
              // TODO: type
              ...allErrors,
              [currentError.path]: {
                type: currentError.type ?? "validation",
                message: currentError.message,
              },
            }),
            {}
          ),
        };
      }
    },
    [validationSchema, validationOptions]
  );
}

/**
 * This function will take an object and omit the values that satisfy the predicate
 * @param object the object on which to execute the operation
 * @param predicate a function that determines whether we omit the value or not
 * @returns new object without the ommitted values
 */
export function omitBy<
  KeyType extends string,
  ObjectType extends Record<KeyType, any>
>(object: ObjectType, predicate: (value: any, key: KeyType) => boolean) {
  return reduceObject(
    object,
    (acc, val, key) =>
      predicate(val, key)
        ? acc
        : {
          ...acc,
          [key]: val,
        },

    {}
  );
}

/**
 * Get Option will turn the fetchable into an Option (monad)
 * @param fetchable The fetchable
 * @returns Some or None
 */
export function getOption<X>(
  fetchable: Fetchable<X>
): Option<FetchableSuccess<X>["data"]> {
  return fetchable.status === "ready" ? Some(fetchable.data) : None;
}

/**
 * Format Ether will format SKRT into readable concise format e.g. 30,000,000 -> 30m
 */
const MillionEther = new BN("1000000000000000000000000");
const ThousandEther = new BN("1000000000000000000000");
const EtherDecPlaces = 18;

export const formatEther = (b: BN) => {
  let decimalPlaces = 0;
  if (b.gte(MillionEther)) decimalPlaces = 6;
  else if (b.gte(ThousandEther)) decimalPlaces = 3;

  let denomination = "";
  if (b.gte(MillionEther)) denomination = "M";
  else if (b.gte(ThousandEther)) denomination = "K";

  let arr = b.toString().split("");
  arr = [...Array(Math.max(0, 19 - arr.length)).fill("0"), ...arr];
  const l = EtherDecPlaces + decimalPlaces - 1;
  const front = arr.length - l;
  let firstpart = arr.slice(0, front);

  firstpart = (
    Math.round(parseFloat([...firstpart, ".", arr[front]].join(""))) + ""
  ).split("");

  // It may be that as a result of rounding we have bigger denomination.
  if (firstpart.length > 4 && decimalPlaces < 6) {
    if (decimalPlaces === 0) {
      denomination = "K";
      decimalPlaces = 3;
    } else if (decimalPlaces === 3) {
      denomination = "M";
      decimalPlaces = 6;
    }
    firstpart = firstpart.slice(0, firstpart.length - 3);
  }
  const afterDot = firstpart.pop();
  return [
    ...(firstpart.length ? firstpart : ["0"]),
    ".",
    afterDot,
    denomination,
  ].join("");
};

const numFormatSample = (1000.1).toLocaleString();
const decimalSeparator = numFormatSample.charAt(5);
const thousandsSeparator = numFormatSample.charAt(1);

export const formatDecimal = (b: BN, decimalPlaces = 18) => {
  let bnString = b.toString(10).split("");

  if (bnString.length < decimalPlaces)
    bnString = Array(decimalPlaces - bnString.length)
      .fill("0")
      .concat(bnString);

  let rear = bnString.splice(bnString.length - decimalPlaces, decimalPlaces);
  while (rear[rear.length - 1] === "0") rear.pop();

  let front =
    bnString.reduceRight((prev, curr, index) => {
      if ((bnString.length - index) % 3 || index === 0) return curr + prev;
      return thousandsSeparator + curr + prev;
    }, "") || "0";

  return rear.length > 0 ? front + decimalSeparator + rear.join("") : front;
};

type Payload<
  K extends { country: typeof countries.T },
  T extends { content: Array<K> }
> = Fetchable<T>;

export function transformActionWithCountry<
  A extends { country: typeof countries.T },
  B extends { content: Array<A> }
>(payload: Payload<A, B>): Payload<A, B> {
  if (payload.status !== "ready") return payload;
  return produce(payload, (p) => {
    p.data.content
      .filter((x) => !!x.country)
      .forEach((item) => {
        item.country = item.country
          ? (item.country.toUpperCase() as typeof countries.T)
          : item.country;
      });
  });
}

export type NullablePartial<T> = { [X in keyof T]?: T[X] | undefined | null };

export function toDecimal(x: number) {
  return (Math.round(x * Math.pow(10, 2)) / Math.pow(10, 2)).toFixed(2);
}

export function deepRemoveEmptyObjects(x: any): any {
  if (!isPlainObject(x)) return x;
  return reduceObject(
    x,
    (acc, val, key) => {
      const newVal = deepRemoveEmptyObjects(val);
      if (!isPlainObject(newVal) || Object.keys(newVal).length > 0) {
        return { ...acc, [key]: newVal };
      }
      return acc;
    },
    {}
  );
}

export function dataURLtoFile(dataurl: string, filename: string) {
  const arr = dataurl.split(",");
  const mime = arr[0]?.match(/:(.*?);/)?.[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
}

/**
 * Lighten or Darken Color
 *
 * The CSS preprocessors Sass and LESS can take any color and darken() or
 * lighten() it by a specific value. But no such ability is built into
 * JavaScript. This function takes colors in hex format (i.e. #F06D06, with or
 * without hash) and lightens or darkens them with a value.
 *
 * @param {String} colorCode The hex color code (with or without # prefix).
 * @param {Int} amount
 */
export function lightenDarkenColor(colorCode: string, amount: number) {
  let usePound = false;

  if (colorCode[0] === "#") {
    colorCode = colorCode.slice(1);
    usePound = true;
  }

  let alpha = "";

  // we have 8 chars
  if (colorCode.length > 6) {
    alpha = colorCode.slice(6);
    colorCode = colorCode.slice(0, 6);
  }

  const num = parseInt(colorCode, 16);

  let r = (num >> 16) + amount;

  if (r > 255) {
    r = 255;
  } else if (r < 0) {
    r = 0;
  }

  let b = ((num >> 8) & 0x00ff) + amount;

  if (b > 255) {
    b = 255;
  } else if (b < 0) {
    b = 0;
  }

  let g = (num & 0x0000ff) + amount;

  if (g > 255) {
    g = 255;
  } else if (g < 0) {
    g = 0;
  }

  return (
    (usePound ? "#" : "") + (g | (b << 8) | (r << 16)).toString(16) + alpha
  );
}

export function nFormatter(num: number, digits: number) {
  const lookup = [
    { value: 1, symbol: "" },
    { value: 1e3, symbol: "k" },
    { value: 1e6, symbol: "M" },
    { value: 1e9, symbol: "G" },
    { value: 1e12, symbol: "T" },
    { value: 1e15, symbol: "P" },
    { value: 1e18, symbol: "E" },
  ];
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  var item = lookup
    .slice()
    .reverse()
    .find(function (item) {
      return num >= item.value;
    });
  return item
    ? (num / item.value).toFixed(digits).replace(rx, "$1") + item.symbol
    : "0";
}

export function difference<T extends any>(a: Set<T>, b: Set<T>) {
  const result = new Set<T>();

  a.forEach((x) => {
    if (!b.has(x)) {
      result.add(x);
    }
  });

  b.forEach((x) => {
    if (!a.has(x)) {
      result.add(x);
    }
  });

  return result;
}

export type OptionType<T> = T extends Option<infer U> ? U : never;

export function deepOmitBy<T>(
  obj: T,
  predicate: (objSubTree: unknown) => boolean
): DeepPartial<T> {
  if (isPlainObject(obj)) {
    return (mapValues(omitBy(obj, predicate), (x) => {
      return deepOmitBy(x, predicate);
    }) as any) as Partial<T>;
  }
  if (Array.isArray(obj)) {
    return (map(
      obj.filter((x) => !predicate(x)),
      (x) => deepOmitBy(x, predicate)
    ) as any) as DeepPartial<T>;
  }
  return obj;
}

export type MakeOptional<Type, Key extends keyof Type> = Omit<Type, Key> &
  Partial<Pick<Type, Key>>;

export function isDefined<T extends any>(x: T): x is Exclude<T, undefined> {
  return x !== undefined;
}
